"use client";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import { isMountedPlacement, placementPresentation } from "@/lib/ads/themeInventory";
import AdRenderer from "./ads/AdRenderer";
import { useConsent } from "@/components/privacy/ConsentRuntime";
import { trackAdEvent } from "@/lib/ads/telemetryClient";
import { advertisingSettings } from "@/lib/ads/settingsContract";
import { parseResponsiveSizes } from "@/lib/ads/responsiveMappings";
import { emitBrowserAdDiagnostic } from "@/lib/ads/deliveryDiagnostics";

export default function AdSlotWrapper({ id, className = "", style = {}, settings = {}, children = null, presentation }) {
  const pathname = usePathname();
  const { contract, canRequestAdvertising } = useConsent();
  const consentCheck = useRef(canRequestAdvertising);
  consentCheck.current = canRequestAdvertising;
  const host = useRef(null);
  const [near, setNear] = useState(false);
  const [decision, setDecision] = useState(null);
  const [filled, setFilled] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [fallback, setFallback] = useState(false);
  const [deliveryState, setDeliveryState] = useState("IDLE");
  const [lastFailure, setLastFailure] = useState("");
  const failedCreative = useRef("");
  const retryTimer = useRef(null);
  const allowed = contract.advertisingAllowed && (!settings.templateId || isMountedPlacement(settings.templateId, id));
  useEffect(() => { emitBrowserAdDiagnostic(id, allowed ? deliveryState : "CONSENT_REQUIRED"); }, [id, allowed, deliveryState]);
  const policy = advertisingSettings(settings);
  const sizes = parseResponsiveSizes(policy.inventory?.[id]?.size || policy.slots?.[id]?.size || "320x50");
  const reservation = sizes.length ? Math.max(...sizes.filter(size => typeof window === "undefined" || size[0] <= window.innerWidth).map(size => size[1]), 0) : 50;

  useEffect(() => {
    setDecision(null); setFilled(false); setFallback(false); setAttempt(0); setDeliveryState("IDLE"); setLastFailure(""); failedCreative.current = "";
    return () => clearTimeout(retryTimer.current);
  }, [id, pathname, allowed, contract.personalizedAdvertisingAllowed]);
  useEffect(() => {
    if (!allowed || !host.current) return;
    if (typeof IntersectionObserver === "undefined") { setNear(true); return; }
    const observer = new IntersectionObserver(entries => { if (entries.some(entry => entry.isIntersecting)) { setNear(true); observer.disconnect(); } }, { rootMargin: "600px 0px" });
    observer.observe(host.current);
    return () => observer.disconnect();
  }, [allowed, pathname]);
  useEffect(() => {
    if (!allowed || !near) return;
    if (consentCheck.current && !consentCheck.current()) { setDecision({ format: "NO_FILL" }); setDeliveryState("CONSENT_UNAVAILABLE"); return; }
    const controller = new AbortController();
    const consentChanged = () => { if (consentCheck.current && !consentCheck.current()) controller.abort(); };
    window.addEventListener("surgedome:consent-changed", consentChanged);
    const edition = pathname.match(/^\/(bw|zw|ke|ng)(?:\/|$)/)?.[1];
    const query = new URLSearchParams({ placement: id, width: String(Math.floor(host.current?.clientWidth || window.innerWidth)) });
    if (edition) query.set("edition", edition);
    if (fallback) { query.set("fallback", "1"); if (failedCreative.current) query.set("exclude", failedCreative.current); }
    setFilled(false);
    setDecision(null);
    setDeliveryState(fallback ? "INTERNAL_FALLBACK" : "REQUESTING");
    fetch("/api/ads/decision?" + query, { signal: controller.signal, cache: "no-store" })
      .then(response => { if (!response.ok) throw new Error("Ad unavailable"); return response.json(); })
      .then(value => {
        if (controller.signal.aborted || (consentCheck.current && !consentCheck.current())) return;
        setDecision(value);
        setDeliveryState(value.format === "NO_FILL" ? "NO_FILL" : "SELECTED");
        if (value.trackingToken) trackAdEvent(value.trackingToken, "AD_REQUEST");
        if (value.format === "NO_FILL") {
          if (value.trackingToken) trackAdEvent(value.trackingToken, "NO_FILL");
          if (attempt < 1 && !fallback) retryTimer.current = setTimeout(() => setAttempt(n => n + 1), 30000);
        }
      }).catch(error => { if (error.name !== "AbortError" && !controller.signal.aborted) { setDeliveryState("DECISION_UNAVAILABLE"); setDecision({ format: "NO_FILL" }); if (attempt < 1 && !fallback) retryTimer.current = setTimeout(() => setAttempt(n => n + 1), 30000); } });
    return () => { controller.abort(); clearTimeout(retryTimer.current); window.removeEventListener("surgedome:consent-changed", consentChanged); };
  }, [id, pathname, allowed, contract.personalizedAdvertisingAllowed, near, attempt, fallback]);
  useEffect(() => {
    if (!decision?.expiresAt) return;
    const timer = setTimeout(() => { setFilled(false); setDecision({ format: "NO_FILL" }); setDeliveryState("EXPIRED"); }, Math.max(0, decision.expiresAt - Date.now()));
    return () => clearTimeout(timer);
  }, [decision]);
  const rendered = useCallback(value => { setFilled(value); if (value) setDeliveryState("FILLED"); }, []);
  const retired = useCallback(reason => {
    clearTimeout(retryTimer.current); setFilled(false); setDecision({ format: "NO_FILL" }); setDeliveryState(reason);
  }, []);
  const failed = useCallback((reason = "RENDER_FAILED") => {
    setLastFailure(reason);
    setDeliveryState(reason);
    setFilled(false);
    if (decision?.trackingToken) trackAdEvent(decision.trackingToken, "NO_FILL");
    if (!fallback) { failedCreative.current = decision?.creativeId || ""; setFallback(true); }
    else setDecision({ format: "NO_FILL" });
  }, [decision, fallback]);
  const active = allowed && decision && decision.format !== "NO_FILL";
  const pending = allowed && !decision;
  const variant = placementPresentation(id, presentation);
  const confirmed = Boolean(active && filled);
  useEffect(() => {
    if (variant !== "zone" || !confirmed || !host.current) return;
    const element = host.current;
    // Measure decoration only; never change the width used for ad eligibility
    // or request another decision when the viewport changes.
    const measure = () => {
      const bounds = element.getBoundingClientRect();
      const width = document.documentElement.clientWidth;
      element.style.setProperty("--ad-zone-left", `${-bounds.left}px`);
      element.style.setProperty("--ad-zone-width", `${width}px`);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(document.documentElement);
    for (let ancestor = element; ancestor; ancestor = ancestor.parentElement) observer.observe(ancestor);
    window.addEventListener("resize", measure);
    return () => { observer.disconnect(); window.removeEventListener("resize", measure); };
  }, [variant, confirmed]);
  return <div ref={host} data-ad-slot={id} data-ad-state={confirmed ? "filled" : "empty"} data-ad-delivery-state={deliveryState} data-ad-last-failure={lastFailure || undefined} data-ad-presentation={variant}
    className={`surgedome-ad-slot ${className}`} style={{ ...style, width: "100%", maxWidth: "100%", overflow: variant === "zone" && confirmed ? "visible" : "hidden", minHeight: pending ? reservation : 0,
      ...(variant === "zone" ? { display: "flex", flexDirection: "column", alignItems: "center", paddingInline: 0, paddingBlock: confirmed ? "clamp(16px, 2vw, 28px)" : 0 } : {}),
      ...(!confirmed && !pending ? { height: 0, padding: 0, margin: 0, border: 0 } : {}) }}>
    {variant === "zone" && confirmed && <span className="surgedome-ad-label">ADVERTISEMENT</span>}
    {active && <AdRenderer key={decision.decisionId} decision={decision} onRenderState={rendered} onFailure={failed} onRetire={retired} placementIdentifier={id} />}
    {!active && !pending && children}
  </div>;
}
